import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RiskAlertsFeed = () => {
  const [alerts, setAlerts] = useState([]);
  const [filter, setFilter] = useState('all');

  // Mock alerts data
  const mockAlerts = [
    {
      id: 1,
      type: 'margin_call',
      severity: 'critical',
      title: 'Margin Call Alert',
      message: 'Account margin level has dropped to 85%. Immediate action required.',
      symbol: 'EURUSD',
      timestamp: new Date(Date.now() - 300000),
      isRead: false,
      actions: ['close_position', 'add_margin']
    },
    {
      id: 2,
      type: 'exposure_limit',
      severity: 'warning',
      title: 'Exposure Limit Exceeded',
      message: 'USD exposure has exceeded 80% of the maximum allowed limit.',
      symbol: 'USD',
      timestamp: new Date(Date.now() - 900000),
      isRead: false,
      actions: ['reduce_exposure', 'adjust_limits']
    },
    {
      id: 3,
      type: 'correlation_risk',
      severity: 'warning',
      title: 'High Correlation Risk',
      message: 'Multiple EUR pairs showing high correlation (0.85+). Consider diversification.',
      symbol: 'EUR',
      timestamp: new Date(Date.now() - 1800000),
      isRead: true,
      actions: ['view_correlation', 'rebalance']
    },
    {
      id: 4,
      type: 'var_breach',
      severity: 'critical',
      title: 'VaR Limit Breach',
      message: 'Daily VaR has exceeded the 95% confidence limit by 15%.',
      symbol: 'Portfolio',
      timestamp: new Date(Date.now() - 3600000),
      isRead: false,
      actions: ['reduce_risk', 'review_positions']
    },
    {
      id: 5,
      type: 'drawdown',
      severity: 'warning',
      title: 'Maximum Drawdown Alert',
      message: 'Account drawdown has reached 12% of peak equity.',
      symbol: 'Account',
      timestamp: new Date(Date.now() - 7200000),
      isRead: true,
      actions: ['stop_trading', 'review_strategy']
    },
    {
      id: 6,
      type: 'position_size',
      severity: 'info',
      title: 'Large Position Alert',
      message: 'Position size on GBPJPY exceeds 5% of account equity.',
      symbol: 'GBPJPY',
      timestamp: new Date(Date.now() - 10800000),
      isRead: true,
      actions: ['reduce_size', 'set_stop_loss']
    }
  ];

  useEffect(() => {
    setAlerts(mockAlerts);
  }, []);

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800';
      case 'warning': return 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950 border-amber-200 dark:border-amber-800';
      case 'info': return 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800';
      default: return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return 'AlertTriangle';
      case 'warning': return 'AlertCircle';
      case 'info': return 'Info';
      default: return 'Bell';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'margin_call': return 'DollarSign';
      case 'exposure_limit': return 'Target';
      case 'correlation_risk': return 'GitBranch';
      case 'var_breach': return 'TrendingDown';
      case 'drawdown': return 'ArrowDown';
      case 'position_size': return 'Maximize';
      default: return 'Bell';
    }
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    
    if (minutes < 60) {
      return `${minutes}m ago`;
    } else if (hours < 24) {
      return `${hours}h ago`;
    } else {
      return timestamp?.toLocaleDateString();
    }
  };

  const filteredAlerts = alerts?.filter(alert => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !alert?.isRead;
    return alert?.severity === filter;
  });

  const markAsRead = (alertId) => {
    setAlerts(prev => prev?.map(alert => 
      alert?.id === alertId ? { ...alert, isRead: true } : alert
    ));
  };

  const handleQuickAction = (alertId, action) => {
    console.log(`Quick action: ${action} for alert ${alertId}`);
    // In a real app, this would trigger the appropriate action
    markAsRead(alertId);
  };

  const clearAllAlerts = () => {
    setAlerts(prev => prev?.map(alert => ({ ...alert, isRead: true })));
  };

  const unreadCount = alerts?.filter(alert => !alert?.isRead)?.length;

  return (
    <div className="bg-card rounded-lg border border-border">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Icon name="AlertTriangle" size={20} className="text-primary" />
            <h2 className="text-lg font-semibold text-foreground">Risk Alerts</h2>
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-xs font-medium px-2 py-1 rounded-full">
                {unreadCount}
              </span>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAllAlerts}
            iconName="CheckCheck"
            className="text-xs"
          >
            Mark All Read
          </Button>
        </div>

        {/* Filter Tabs */}
        <div className="flex space-x-1 bg-muted rounded-lg p-1">
          {[
            { key: 'all', label: 'All', count: alerts?.length },
            { key: 'critical', label: 'Critical', count: alerts?.filter(a => a?.severity === 'critical')?.length },
            { key: 'warning', label: 'Warning', count: alerts?.filter(a => a?.severity === 'warning')?.length },
            { key: 'unread', label: 'Unread', count: unreadCount }
          ]?.map((tab) => (
            <button
              key={tab?.key}
              onClick={() => setFilter(tab?.key)}
              className={`flex items-center space-x-1 px-3 py-2 text-xs font-medium rounded-md transition-colors duration-200 ${
                filter === tab?.key
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <span>{tab?.label}</span>
              {tab?.count > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  filter === tab?.key 
                    ? 'bg-primary-foreground/20 text-primary-foreground' 
                    : 'bg-muted-foreground/20 text-muted-foreground'
                }`}>
                  {tab?.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
      {/* Alerts List */}
      <div className="max-h-96 overflow-y-auto">
        {filteredAlerts?.length === 0 ? (
          <div className="p-8 text-center">
            <Icon name="CheckCircle" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No alerts to display</p>
          </div>
        ) : (
          <div className="space-y-1 p-2">
            {filteredAlerts?.map((alert) => (
              <div
                key={alert?.id}
                className={`p-4 rounded-lg border transition-all duration-200 hover:shadow-sm ${
                  alert?.isRead 
                    ? 'bg-muted/30 border-border opacity-75' 
                    : getSeverityColor(alert?.severity)
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-1">
                    <Icon 
                      name={getSeverityIcon(alert?.severity)} 
                      size={16} 
                      className={alert?.isRead ? 'text-muted-foreground' : ''}
                    />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className={`text-sm font-medium ${
                        alert?.isRead ? 'text-muted-foreground' : 'text-foreground'
                      }`}>
                        {alert?.title}
                      </h4>
                      <div className="flex items-center space-x-2">
                        <Icon name={getTypeIcon(alert?.type)} size={14} className="text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">
                          {formatTimestamp(alert?.timestamp)}
                        </span>
                      </div>
                    </div>
                    
                    <p className={`text-xs mb-2 ${
                      alert?.isRead ? 'text-muted-foreground' : 'text-muted-foreground'
                    }`}>
                      {alert?.message}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-mono bg-muted px-2 py-1 rounded text-muted-foreground">
                          {alert?.symbol}
                        </span>
                        {!alert?.isRead && (
                          <span className="w-2 h-2 bg-primary rounded-full"></span>
                        )}
                      </div>
                      
                      {alert?.actions && alert?.actions?.length > 0 && (
                        <div className="flex space-x-1">
                          {alert?.actions?.slice(0, 2)?.map((action) => (
                            <Button
                              key={action}
                              variant="ghost"
                              size="xs"
                              onClick={() => handleQuickAction(alert?.id, action)}
                              className="text-xs h-6 px-2"
                            >
                              {action?.replace('_', ' ')}
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RiskAlertsFeed;